import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getFormattedNewBalance } from '@/services/billing'
import { DollarSign, ArrowRight, PlusCircle, LucideIcon } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { presets } from './consts'
import { DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { ErrorMessage } from '@/components/error-message'
import { LoadingMessage } from '@/components/loading-message'
import { FormattedOrgBalanceSummary } from '@/services/home'
import { FormattedOrgUserBalanceSummary } from '@/services/billing'
import {
  OrgBalanceDetailsDialogProps,
  UserBalanceDetailsDialogProps,
} from '@/components/balance-details-dialog'

// Form validation schema
const TopUpFormSchema = z.object({
  amount: z
    .number({ invalid_type_error: 'Please enter a valid amount' })
    .min(10, 'Minimum amount is $10')
    .int('Only whole amounts are allowed')
    .positive('Amount must be positive')
    .optional(),
})

type TopUpFormValues = z.infer<typeof TopUpFormSchema>

export interface TopUpFormConfig {
  // Data hooks
  useBalanceData: (userId: number) => {
    data:
      | FormattedOrgBalanceSummary
      | FormattedOrgUserBalanceSummary
      | undefined
    isLoading: boolean
    isError: boolean
    error: Error | null
  }
  useCreatePayment: () => {
    mutate: (
      params: { amount: number },
      options?: {
        onSuccess?: (response: { url: string; id: string }) => void
        onError?: (error: Error) => void
      }
    ) => void
    isPending: boolean
  }

  // UI config
  icon: LucideIcon
  title: string
  loadingMessage: string
  errorMessagePrefix: string
  noDataMessage: string

  // Balance details dialog
  BalanceDetailsDialog: React.ComponentType<{
    trigger: React.ReactNode
    data: OrgBalanceDetailsDialogProps | UserBalanceDetailsDialogProps
  }>
}

export interface TopUpFormProps {
  config: TopUpFormConfig
  userId: number
  variant?: 'display-only' | 'full'
  onClose?: () => void
}

export const TopUpForm = ({
  config,
  userId,
  variant = 'full',
  onClose,
}: TopUpFormProps) => {
  const [animatedBalance, setAnimatedBalance] = useState<string | null>(null)
  const { mutate: createPayment, isPending } = config.useCreatePayment()

  const form = useForm<TopUpFormValues>({
    resolver: zodResolver(TopUpFormSchema),
    defaultValues: {
      amount: undefined,
    },
    mode: 'onChange',
  })

  const { data, isLoading, isError, error } = config.useBalanceData(userId)
  const watchedAmount = form.watch('amount')

  // Animation effect when amount changes
  useEffect(() => {
    if (data && variant === 'full' && watchedAmount && watchedAmount > 0) {
      setAnimatedBalance(getFormattedNewBalance(data.netBalance, watchedAmount))
    } else {
      setAnimatedBalance(null)
    }
  }, [watchedAmount, data, variant])

  const handleTopUp = (values: TopUpFormValues) => {
    if (!values.amount) return
    createPayment(
      { amount: values.amount },
      {
        onSuccess: (response: { url: string; id: string }) => {
          const stripeUrl = response.id
          if (stripeUrl) {
            window.location.href = stripeUrl
          } else {
            alert('Failed to retrieve Stripe payment URL. Please try again.')
          }
        },
        onError: (error: Error) => {
          console.error('Payment error:', error)
          alert('Failed to initiate payment. Please try again.')
        },
      }
    )
  }

  const handlePillClick = (value: number) => {
    form.setValue('amount', value, { shouldValidate: true })
  }

  if (isLoading) {
    return <LoadingMessage message={config.loadingMessage} centered />
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${config.errorMessagePrefix}: ${error?.toString()}`}
      />
    )
  }

  if (!data) {
    return <ErrorMessage message={config.noDataMessage} />
  }

  const balanceContent = (
    <div className="space-y-4">
      <div>
        {variant === 'display-only' && (
          <div>
            <p className="text-2xl font-bold text-primary">
              {data.formattedNetBalance}
            </p>
          </div>
        )}

        {variant === 'full' && (
          <>
            <div className="flex items-center justify-between text-sm text-foreground/60 mb-2">
              <span>Balance Summary</span>
              <DollarSign className="w-4 h-4" />
            </div>

            <div className="p-4 bg-foreground/5 rounded-lg border">
              {/* Current and New Balance Display */}
              <div className="relative flex justify-between items-baseline min-h-[64px]">
                <div>
                  <span className="text-sm text-foreground/60">Current</span>
                  <p className="text-xl font-semibold">
                    {data.formattedNetBalance}
                  </p>
                </div>

                {watchedAmount && watchedAmount > 0 && (
                  <>
                    <div className="absolute left-1/2 top-[40%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-foreground/60 z-10">
                      <div className="flex items-center bg-background/90 rounded-full px-3 py-1.5 shadow-sm border">
                        <PlusCircle className="w-4 h-4 mr-1.5 flex-shrink-0 text-green-500" />
                        <span className="text-sm font-medium">
                          ${watchedAmount.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div className="text-right">
                      <span className="text-sm text-foreground/60">
                        New Balance
                      </span>
                      <p className="text-xl font-bold text-primary animate-pulse">
                        {animatedBalance}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {variant === 'display-only' && (
        <config.BalanceDetailsDialog
          trigger={
            <Button
              variant="link"
              className="h-auto p-0 text-sm text-foreground/60"
            >
              View Balance Details
              <ArrowRight size={16} className="ml-1" />
            </Button>
          }
          data={{
            ...data,
            usage_percent: data.usage_percent,
            showBillingLink: false,
          }}
        />
      )}
    </div>
  )

  if (variant === 'display-only') {
    return balanceContent
  }

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <config.icon className="w-5 h-5 text-primary" />
          {config.title}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-6">
        {balanceContent}

        <div className="space-y-5">
          <div className="grid grid-cols-4 gap-3">
            {presets.map((preset) => (
              <Button
                key={preset}
                variant={watchedAmount === preset ? 'default' : 'outline'}
                onClick={() => handlePillClick(preset)}
                className={cn('w-full transition-all')}
              >
                ${preset}
              </Button>
            ))}
          </div>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleTopUp)}
              className="space-y-2"
            >
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground/60 w-16 text-right">
                        Custom:
                      </span>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Enter amount"
                          value={field.value || ''}
                          onChange={(e) => {
                            const value = e.target.value
                            const numValue =
                              value === '' ? undefined : Number(value)
                            field.onChange(numValue)
                          }}
                          step="1"
                          min="10"
                          className={cn(
                            'focus-visible:ring-primary',
                            form.formState.errors.amount
                              ? 'border-destructive'
                              : ''
                          )}
                        />
                      </FormControl>
                    </div>
                    <FormMessage className="pl-16" />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>

        <div className="flex items-center justify-end gap-2">
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button
            variant="default"
            onClick={form.handleSubmit(handleTopUp)}
            disabled={
              isPending ||
              !watchedAmount ||
              watchedAmount < 10 ||
              !form.formState.isValid
            }
            type="submit"
          >
            {isPending ? (
              <>
                <LoadingMessage size="sm" className="mr-2 [&>svg]:text-white" />{' '}
                Processing...
              </>
            ) : (
              <>
                Top Up ${watchedAmount ? watchedAmount.toLocaleString() : '0'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
